import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CelestialDataLoader from '../components/starmap/CelestialDataLoader';
import HorizontalTabs from '../components/HorizontalTabs';
import TabPanel from '../components/TabPanel';
import MapPreview from '../components/create/MapPreview';
import ErrorBoundary from '../components/ErrorBoundary';
import styled from 'styled-components';
// import { useDispatch } from 'react-redux';
// import { addToCart } from '../redux/cartSlice';
import { toast } from 'react-hot-toast';
import { getLocalStorageInfo, safeSetItem, getObjectSize, safeAddToCart, optimizeCartItems, monitorMemoryUsage, performMemoryCleanup } from '../utils/storageUtils';
import { buildApiUrl } from '../utils/apiUtils';
import { useCurrency } from '../contexts/CurrencyContext';

// Import step components using the index file
import {
    DesignStep,
    FormatStep,
    MomentStep,
    TextStep,
    PaymentStep
} from '../components/create/steps';

// Lightweight preview generator for cart (memory-efficient)
const generateLightweightPreview = (config, locationName) => {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Use small canvas size for memory efficiency
        canvas.width = 300;
        canvas.height = 400;

        // Background color mapping
        const getBackgroundColor = (bgType) => {
            const backgrounds = {
                'gray': '#808080',
                'black': '#000000',
                'sunset-pink': '#2D1B3D',
                'sunset-orange': '#3D2415',
                'golden-black': '#1A1611',
                'neon-blue': '#0B1A2D',
                'pastel-mint': '#152D25',
                'harmony-1': '#1a1a2e',
                'harmony-2': '#2e1a1a',
                'natural-1': '#1a2e1a',
                'natural-2': '#2e2e1a',
                'golden-elegance-1': '#2e1a0f',
                'golden-elegance-2': '#1a1611',
                'neon-1': '#0B1A2D',
                'neon-2': '#1A0B2D',
                'pastel-1': '#152D25',
                'pastel-2': '#2D1525',
                'sunset-1': '#2D1B3D',
                'sunset-2': '#3D2415'
            };
            return backgrounds[bgType] || '#000000';
        };

        // Fill background
        const backgroundColor = getBackgroundColor(config.background);
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add stars if enabled
        if (config.showStars !== false) {
            ctx.fillStyle = '#FFFFFF';
            for (let i = 0; i < 30; i++) {
                const x = Math.random() * (canvas.width - 40) + 20;
                const y = Math.random() * (canvas.height - 100) + 20;
                const size = Math.random() * 1.5 + 0.5;
                const opacity = Math.random() * 0.8 + 0.2;
                ctx.globalAlpha = opacity;
                ctx.beginPath();
                ctx.arc(x, y, size, 0, 2 * Math.PI);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
        }

        // Add constellation lines if enabled
        if (config.showConstellations !== false) {
            ctx.strokeStyle = '#CCCCCC';
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.4;

            const patterns = [
                [[100, 80], [140, 100], [180, 90]],
                [[220, 120], [260, 110], [280, 130]],
                [[80, 150], [120, 170], [160, 160]],
                [[200, 180], [240, 200], [270, 190]]
            ];

            patterns.forEach(pattern => {
                ctx.beginPath();
                ctx.moveTo(pattern[0][0], pattern[0][1]);
                for (let i = 1; i < pattern.length; i++) {
                    ctx.lineTo(pattern[i][0], pattern[i][1]);
                }
                ctx.stroke();
            });
            ctx.globalAlpha = 1;
        }

        // Add circular frame
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2 - 20;
        const radius = Math.min(canvas.width, canvas.height) * 0.35;

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Add text
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(config.caption || 'Star Map', canvas.width / 2, canvas.height - 70);

        ctx.font = '12px Arial';
        if (config.date) {
            const date = new Date(config.date);
            const dateStr = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
            ctx.fillText(dateStr, canvas.width / 2, canvas.height - 50);
        }
        if (locationName) {
            ctx.fillText(locationName, canvas.width / 2, canvas.height - 30);
        }

        // Convert to data URL with good compression
        return canvas.toDataURL('image/png', 0.6);
    } catch (error) {
        console.warn('Error generating lightweight preview:', error);
        return '/images/products/default-map.jpg';
    }
};

// Helper function to generate a preview image based on configuration
const generateConfigBasedPreview = (config, locationName) => {
    try {
        // Create a canvas to generate a preview
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size
        canvas.width = 400;
        canvas.height = 500;

        // Background color based on config
        let bgColor = '#000000'; // Default black
        switch (config.background) {
            case 'gray':
                bgColor = '#808080';
                break;
            case 'black':
                bgColor = '#000000';
                break;
            case 'sunset-pink':
                bgColor = '#2D1B3D';
                break;
            case 'sunset-orange':
                bgColor = '#3D2415';
                break;
            case 'golden-black':
                bgColor = '#1A1611';
                break;
            case 'neon-blue':
                bgColor = '#0B1A2D';
                break;
            case 'pastel-mint':
                bgColor = '#152D25';
                break;
            default:
                bgColor = '#000000';
        }

        // Fill background
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add stars if enabled
        if (config.showStars) {
            ctx.fillStyle = '#FFFFFF';
            for (let i = 0; i < 50; i++) {
                const x = Math.random() * (canvas.width - 40) + 20;
                const y = Math.random() * (canvas.height - 100) + 20;
                const size = Math.random() * 2 + 1;
                ctx.beginPath();
                ctx.arc(x, y, size, 0, 2 * Math.PI);
                ctx.fill();
            }
        }

        // Add constellation lines if enabled
        if (config.showConstellations) {
            ctx.strokeStyle = '#CCCCCC';
            ctx.lineWidth = 1;

            // Draw some constellation-like patterns
            const patterns = [
                [[100, 100], [150, 120], [200, 110]],
                [[250, 150], [300, 140], [350, 160], [320, 190]],
                [[80, 200], [120, 220], [160, 210], [140, 250]]
            ];

            patterns.forEach(pattern => {
                ctx.beginPath();
                ctx.moveTo(pattern[0][0], pattern[0][1]);
                for (let i = 1; i < pattern.length; i++) {
                    ctx.lineTo(pattern[i][0], pattern[i][1]);
                }
                ctx.stroke();
            });
        }

        // Add title and details
        ctx.fillStyle = '#FFD700'; // Gold
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(config.caption || 'Star Map', canvas.width / 2, canvas.height - 80);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '14px Arial';
        if (config.date) {
            ctx.fillText(config.date, canvas.width / 2, canvas.height - 55);
        }
        if (locationName) {
            ctx.fillText(locationName, canvas.width / 2, canvas.height - 35);
        }

        // Add frame indication if printed
        if (config.printType === 'printed' && config.frame !== 'none') {
            ctx.strokeStyle = config.frame === 'Classic Black' ? '#333333' : '#CCCCCC';
            ctx.lineWidth = 3;
            ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
        }

        // Convert to data URL
        return canvas.toDataURL('image/png', 0.8);
    } catch (error) {
        console.warn('Error generating config-based preview:', error);
        return '/images/products/default-map.jpg';
    }
};

const CreateMapPage = () => {
    const navigate = useNavigate();
    const { formatPrice } = useCurrency();
    // const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        date: '',
        location: '',
        caption: '',
        mapStyle: 'classic',
        textStyle: 'classic',
        size: '21cm x 30cm',
        frame: 'Classic Black',
        printType: 'digital', // Added print type: digital or printed
        // Display options for celestial objects
        showStars: true,
        showConstellations: true,
        showConstellationNames: true,
        showGrid: true,
        showDeepSkyObjects: true,
        showEcliptic: true,
        background: null,
        orbitalStyle: 'classic'
    });
    const [cityName, setCityName] = useState('');
    const [errors, setErrors] = useState({});
    const [mapGenerated, setMapGenerated] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [addedToCart, setAddedToCart] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [mapError, setMapError] = useState(null);
    // Add a counter to force map re-renders
    const [mapRenderKey, setMapRenderKey] = useState(0);

    // Add the missing starMapRef
    const starMapRef = useRef(null);

    // Ref for scroll target at the top of the map creation section
    const mapCreationSectionRef = useRef(null);

    // Ref to track if map should be regenerated on data change
    const shouldRegenerateMap = useRef(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        console.log('🔍 DEBUG: handleInputChange called', {
            name,
            value,
            currentFormData: formData,
            currentLocation: formData.location,
            isGenerating,
            mapGenerated
        });

        // Special handling for date changes to ensure smooth loading
        if (name === 'date') {
            console.log('📅 DEBUG: Date change detected', {
                oldDate: formData.date,
                newDate: value,
                hasLocation: !!formData.location,
                locationValue: formData.location
            });

            // Always set regeneration flag for date changes to ensure astronomical accuracy
            shouldRegenerateMap.current = true;
            console.log('🔄 Parent: Set shouldRegenerateMap flag for date change:', value);

            // Start loading animation
            setIsGenerating(true);
            setLoadingProgress(0);

            // Use requestAnimationFrame for ultra-smooth animation
            let progress = 0;
            const animateProgress = () => {
                progress += 2; // Smaller increments for smoother animation
                setLoadingProgress(Math.min(progress, 80)); // Cap at 80% until fully loaded

                if (progress < 80) {
                    requestAnimationFrame(animateProgress);
                }
            };
            requestAnimationFrame(animateProgress);

            // UPDATE IMMEDIATELY - no setTimeout delay
            console.log('🔍 DEBUG: About to update formData with new date', {
                currentFormData: formData,
                newDateValue: value,
                regenerateFlag: shouldRegenerateMap.current,
                hasUserLocation: !!(formData.location && formData.location.trim() && formData.location.includes(','))
            });

            setFormData(prev => {
                const newFormData = {
                    ...prev,
                    [name]: value
                };
                console.log('🔍 DEBUG: FormData updated IMMEDIATELY', {
                    oldFormData: prev,
                    newFormData,
                    hasLocation: !!newFormData.location,
                    newDateValue: newFormData.date
                });
                return newFormData;
            });
        } else {
            // For non-date fields, update immediately without regeneration flag
            console.log('🔍 DEBUG: Non-date field update', { name, value });
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePrintTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            printType: type
        }));
    };

    const handleLocationSelect = (coordinates, city) => {
        console.log('🎯 handleLocationSelect called:', { coordinates, city });

        setCityName(city);

        // Start the loading animation FIRST, before updating formData
        setIsGenerating(true);
        setLoadingProgress(0);

        // Use requestAnimationFrame for ultra-smooth loading animation
        let progress = 0;
        const animateProgress = () => {
            progress += 1.5; // Ultra-smooth increments
            setLoadingProgress(Math.min(progress, 85)); // Cap at 85% until fully loaded

            if (progress < 85) {
                requestAnimationFrame(animateProgress);
            }
        };
        requestAnimationFrame(animateProgress);

        // Short delay before updating formData to allow animation to start
        setTimeout(() => {
            // Flag that we need to regenerate the map with the new location
            shouldRegenerateMap.current = true;

            console.log('📝 About to update formData with:', { coordinates, city });

            // Update the formData with new location and city name
            setFormData(prev => {
                const newFormData = {
                    ...prev,
                    location: coordinates,
                    locationName: city // Store the city name separately
                };
                console.log('📝 Updated formData:', newFormData);
                return newFormData;
            });

            // Force component remounting by incrementing the key
            setMapRenderKey(prevKey => prevKey + 1);
        }, 100);
    };

    const handleOptionSelect = (optionType, optionId) => {
        setFormData(prev => ({
            ...prev,
            [optionType]: optionId
        }));

        // Auto-advance to next step when orbital style (design) is selected
        if (optionType === 'orbitalStyle' && activeTab === 0) {
            // Small delay to allow the selection to be visually confirmed
            setTimeout(() => {
                setActiveTab(1); // Move to Format tab (index 1)
            }, 300);
        }
    };

    const handleToggleCelestialElement = (elementName) => {
        // Update the formData state
        setFormData(prev => {
            const newState = {
                ...prev,
                [elementName]: !prev[elementName]
            };

            // Update Celestial settings if available
            if (window.Celestial && typeof window.Celestial.apply === 'function') {
                try {
                    // Create the proper update structure based on what's being toggled
                    const updateConfig = {};

                    // Set the correct path based on the element being toggled
                    switch (elementName) {
                        case 'showStars':
                            updateConfig.stars = { show: !prev[elementName] };
                            break;
                        case 'showConstellations':
                            updateConfig.constellations = {
                                lines: !prev[elementName],
                                // Include the lineStyle to maintain our constellation line appearance
                                lineStyle: { stroke: "#8eb4e3", opacity: 0.3, width: 1 }
                            };
                            break;
                        case 'showConstellationNames':
                            updateConfig.constellations = {
                                names: !prev[elementName],
                                // Include the lineStyle to maintain our constellation line appearance
                                lineStyle: { stroke: "#8eb4e3", opacity: 0.3, width: 1 }
                            };
                            break;
                        case 'showGrid':
                            updateConfig.lines = {
                                graticule: { show: !prev[elementName] },
                                equatorial: { show: !prev[elementName] }
                            };
                            break;
                        case 'showDeepSkyObjects':
                            updateConfig.dsos = { show: !prev[elementName] };
                            break;
                        case 'showEcliptic':
                            updateConfig.lines = {
                                ecliptic: { show: !prev[elementName] }
                            };
                            break;
                        default:
                            break;
                    }

                    // Apply the changes
                    window.Celestial.apply(updateConfig);

                    // Redraw without reloading data
                    window.Celestial.redraw();
                } catch (error) {
                    console.error('Error updating Celestial settings:', error);
                }
            }

            return newState;
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.date) newErrors.date = 'Date is required';
        if (!formData.location) newErrors.location = 'Location is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate if the Moment step can proceed to next step
    const canProceedFromMomentStep = () => {
        return formData.date && formData.location;
    };

    // Scroll to the top of the map creation section
    const scrollToMapCreationTop = () => {
        setTimeout(() => {
            const scrollTarget = mapCreationSectionRef.current;

            if (scrollTarget) {
                scrollTarget.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });

                // Additional adjustment to show step names
                setTimeout(() => {
                    const currentScrollY = window.pageYOffset;
                    window.scrollTo({
                        top: Math.max(0, currentScrollY - 220),
                        behavior: 'smooth'
                    });
                }, 200);
            }
        }, 100);
    };

    const handleGenerateMap = () => {
        if (validateForm()) {
            setIsGenerating(true);
            setLoadingProgress(0);
            shouldRegenerateMap.current = true;

            // Use requestAnimationFrame for ultra-smooth loading animation
            let progress = 0;
            const animateProgress = () => {
                progress += 2; // Ultra-smooth increments
                setLoadingProgress(Math.min(progress, 82)); // Cap at 82% until fully loaded

                if (progress < 82) {
                    requestAnimationFrame(animateProgress);
                }
            };
            requestAnimationFrame(animateProgress);

            // Map generation will complete in StarMapGenerator component
            // The handleStarMapLoaded callback will finish the progress and hide the loader
        }
    };

    // 🚀 REMOVED: Old wrapper function replaced with direct Extract SVG button approach

    const handleAddToCart = async () => {
        const errors = validateForm(true);

        if (Object.keys(errors).length === 0) {
            setIsAddingToCart(true);

            try {
                // SAME CHECK AS EXTRACT SVG BUTTON - Only proceed if map is ready
                if (!mapGenerated || isGenerating) {
                    console.warn('🚫 Star map is not ready yet. Please wait for generation to complete before adding to cart.');
                    setIsAddingToCart(false);
                    toast.error('Please wait for the star map to finish generating before adding to cart.');
                    return;
                }

                // Generate a unique map ID
                const mapId = 'map-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);

                // Calculate price
                const price = calculatePrice();

                // REMOVED: generateLightweightPreview - causes memory issues
                // Use default image instead - star map will be generated properly via SVG extraction
                let capturedImageUrl = '/images/products/default-map.jpg';

                // Debug location data
                console.log('🗺️ Location Debug:', {
                    'formData.location': formData.location,
                    'formData.locationName': formData.locationName,
                    'cityName': cityName,
                    'finalLocationName': formData.locationName || cityName
                });

                // Create cart item with captured image
                const cartItem = {
                    id: mapId,
                    name: formData.caption || 'Custom Star Map',
                    price: price,
                    quantity: 1,
                    imageUrl: capturedImageUrl,
                    // Include essential customization data
                    date: formData.date,
                    location: formData.location, // Store coordinates
                    locationName: formData.locationName || cityName, // Store city name for display (prefer formData)
                    caption: formData.caption,
                    size: formData.size,
                    frame: formData.frame,
                    printType: formData.printType,
                    // Store complete configuration for backend processing
                    mapConfig: {
                        date: formData.date,
                        location: formData.location,
                        caption: formData.caption,
                        size: formData.size,
                        background: formData.background,
                        orbitalStyle: formData.orbitalStyle,
                        showStars: formData.showStars,
                        showConstellations: formData.showConstellations,
                        showConstellationNames: formData.showConstellationNames,
                        showGrid: formData.showGrid,
                        showDeepSkyObjects: formData.showDeepSkyObjects,
                        showEcliptic: formData.showEcliptic
                    },
                    // Mark as requiring backend processing
                    requiresProcessing: true
                };

                // Add star map reference for file generation
                cartItem.starMapRef = starMapRef;

                // Simplified add to cart - just try to add it
                const success = await safeAddToCart(cartItem);

                if (success) {
                    window.dispatchEvent(new Event('cartUpdated'));

                    // 🚀 SIMPLIFIED: Extract SVG immediately since we know map is ready
                    console.log('🎯 Starting SVG extraction (map is ready)...');

                    // Extract SVG in background after cart addition completes
                    setTimeout(async () => {
                        try {
                            console.log('⏳ Starting SVG extraction (same as Extract SVG button)...');

                            // EXACT same 2-second delay as Extract SVG button
                            await new Promise(resolve => setTimeout(resolve, 2000));

                            // EXACT same call as Extract SVG button
                            const extractedSVG = await starMapRef.current.extractCompleteMapPreview(formData.background);

                            if (extractedSVG) {
                                console.log('✅ SVG extraction successful for cart item');

                                // Convert to string and save
                                const svgString = new XMLSerializer().serializeToString(extractedSVG);
                                const svgDataUri = 'data:image/svg+xml;base64,' + btoa(svgString);

                                // Debug save data
                                const saveData = {
                                    temp_order_id: cartItem.id,
                                    svgData: svgDataUri,
                                    captureMethod: 'extract-svg-approach',
                                    ...cartItem.mapConfig,
                                    caption: cartItem.caption,
                                    date: cartItem.date,
                                    location: cartItem.location,
                                    locationName: cartItem.locationName || formData.locationName, // Include city name
                                    size: cartItem.size,
                                    frame: cartItem.frame,
                                    printType: cartItem.printType,
                                    background: cartItem.mapConfig?.background || 'golden-black'
                                };

                                console.log('💾 Save Data Debug:', {
                                    'location': saveData.location,
                                    'locationName': saveData.locationName,
                                    'cartItem.locationName': cartItem.locationName,
                                    'formData.locationName': formData.locationName
                                });

                                // Save to server
                                const saveResponse = await fetch(buildApiUrl('save-starmap'), {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(saveData)
                                });

                                const saveResult = await saveResponse.json();
                                if (saveResult.success) {
                                    console.log('✅ Cart item file saved successfully');
                                } else {
                                    console.warn('⚠️ Failed to save cart item file:', saveResult.message);
                                }
                            } else {
                                console.error('❌ SVG extraction failed for cart item');
                            }
                        } catch (error) {
                            console.error('❌ Cart item file generation error:', error);
                        }
                    }, 100); // Small delay to let UI update first

                    setIsAddingToCart(false);
                    setAddedToCart(true);
                    toast.success('Added to cart! Files are being prepared in the background.');
                } else {
                    throw new Error('Failed to add to cart');
                }

            } catch (error) {
                console.error('Error adding to cart:', error);
                setIsAddingToCart(false);
                toast.error('Failed to add to cart. Please try again.');
            }
        } else {
            // Handle invalid form
            setIsAddingToCart(false);
            toast.error('Please fill in all required fields');
        }
    };

    const calculatePrice = () => {
        // Base price depends on print type and size
        let price = 0;

        // Digital download sizes and prices
        const digitalSizePrices = {
            '21cm x 30cm': 10.00,
            '30cm x 40cm': 15.00,
            '60cm x 90cm': 20.00
        };

        // Printed sizes and prices
        const printedSizePrices = {
            '21cm x 30cm': 50.00,
            '30cm x 40cm': 75.00,
            '60cm x 90cm': 100.00
        };

        // Add base price depending on print type and size
        if (formData.printType === 'digital') {
            price += digitalSizePrices[formData.size] || 10.00;
        } else {
            price += printedSizePrices[formData.size] || 50.00;

            // Add frame price for printed maps only
            if (formData.frame === 'Classic Black' || formData.frame === 'Classic White') {
                price += 50.00;
            } else if (formData.frame === 'Silver') {
                price += 60.00;
            }
        }

        // Add price based on background
        if (formData.background) {
            // Map background options to their prices
            const backgroundPrices = {
                'basic-light': 5.00,
                'basic-dark': 5.00,
                'sunset-pink': 8.00,
                'sunset-orange': 8.00,
                'pastel-pink': 6.50,
                'pastel-blue': 6.50,
                'pastel-white': 6.50,
                'neon-purple': 9.00,
                'neon-blue': 9.00,
                'natural-gold': 7.00,
                'natural-brown': 7.00,
                'natural-beige': 7.00,
                'golden-dark': 11.00,
                'golden-black': 11.00,
                'golden-gold': 11.00
            };

            price += backgroundPrices[formData.background] || 0;
        }

        // Add price based on map style
        if (formData.mapStyle) {
            // Map style options to their prices
            const mapStylePrices = {
                'classic': 0.00,  // Base price already includes classic style
                'minimal': 0.00,
                'vintage': 15.00,
                'cosmic': 15.00,
                'modern': 15.00,
                'nebula': 25.00,
                'midnight': 25.00,
                'constellation': 25.00
            };

            price += mapStylePrices[formData.mapStyle] || 0;
        }

        // Add price based on orbital style
        if (formData.orbitalStyle) {
            // Map orbital style options to their prices
            const orbitalStylePrices = {
                'classic': 0.00,
                'minimal': 0.00,
                'geometric': 2.50,
                'celestial': 3.50,
                'cosmic': 4.00,
                'spiral': 3.00,
                'wave': 3.50,
                'orbit': 4.50,
                'heart': 5.50,
                'starburst': 5.50,
                'asymmetric': 5.00,
                'star': 6.00,
                'galaxy': 6.50
            };

            price += orbitalStylePrices[formData.orbitalStyle] || 0;
        }

        // Add price based on celestial elements
        const celestialElementPrices = {
            'showStars': 4.00,
            'showConstellations': 5.00,
            'showConstellationNames': 5.00,
            'showGrid': 8.00,
            'showDeepSkyObjects': 13.00,
            'showEcliptic': 17.50
        };

        // Add the prices for enabled celestial elements
        Object.keys(celestialElementPrices).forEach(element => {
            if (formData[element]) {
                price += celestialElementPrices[element];
            }
        });

        return price.toFixed(2);
    };

    const handleStarMapError = (error) => {
        console.log('Star map error received:', error);

        // Don't treat "loading" messages as actual errors
        if (error && (error.includes('Loading high-quality') || error.includes('Please wait'))) {
            console.log('This is a loading message, not an error - continuing to wait');
            return; // Don't set this as an error, it's just a loading message
        }

        setMapError(error);
        setIsGenerating(false);
        setLoadingProgress(0);

        // Reset the regenerate flag when there's an error
        shouldRegenerateMap.current = false;
        console.log('🔄 Parent: Reset shouldRegenerateMap flag after error');
    };

    const handleStarMapLoaded = () => {
        console.log('Star map loaded successfully');
        setMapGenerated(true);
        setMapError(null); // Clear any previous errors
        setIsGenerating(false);

        // Reset the regenerate flag after successful load
        shouldRegenerateMap.current = false;
        console.log('🔄 Parent: Reset shouldRegenerateMap flag after successful load');

        // Smooth completion animation
        const completeAnimation = () => {
            let progress = loadingProgress;
            const animate = () => {
                progress += 3;
                setLoadingProgress(Math.min(progress, 100));

                if (progress < 100) {
                    requestAnimationFrame(animate);
                } else {
                    // Final cleanup after animation
                    setTimeout(() => {
                        setLoadingProgress(100);
                        setIsGenerating(false);
                    }, 200);
                }
            };
            requestAnimationFrame(animate);
        };

        completeAnimation();
    };

    // Handle tab change
    const handleTabChange = (newValue) => {
        setActiveTab(newValue);
        // Scroll to top of map creation section
        scrollToMapCreationTop();
        // Only regenerate map if it's the first time viewing this tab
        if (!mapGenerated) {
            handleGenerateMap();
        }
    };

    // Generate map on initial load
    useEffect(() => {
        // Start with map generation on component mount
        setIsGenerating(true);
        setLoadingProgress(0);
        shouldRegenerateMap.current = true;

        // Use requestAnimationFrame for ultra-smooth loading animation
        let progress = 0;
        const animateProgress = () => {
            progress += 1.2; // Ultra-smooth increments for initial load
            setLoadingProgress(Math.min(progress, 78)); // Cap at 78% until fully loaded

            if (progress < 78) {
                requestAnimationFrame(animateProgress);
            }
        };
        requestAnimationFrame(animateProgress);

        // The handleStarMapLoaded callback will finish the progress and hide the loader

        return () => {
            // Cleanup is handled by requestAnimationFrame completion
        };
    }, []);

    // Note: Animation completion is now handled in handleStarMapLoaded for smoother transitions

    // Define tabs
    const tabs = [
        { label: 'Design', component: DesignStep },
        { label: 'Format', component: FormatStep },
        { label: 'Moment', component: MomentStep },
        { label: 'Text', component: TextStep },
        { label: 'Payment', component: PaymentStep }
    ];

    return (
        <ErrorBoundary>
            <div className="relative">
                {/* Data Loader for d3-celestial */}
                <CelestialDataLoader
                    onProgressUpdate={(progress) => console.log("Data loading progress:", progress)}
                />

                <div className="relative z-1 -mt-6">
                    <div className="flex flex-col items-center justify-start w-full !bg-cover !bg-center pt-[14vw]" style={{
                        backgroundImage: "url('/images/header-small.webp')",
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                    }}>
                        <h1 className="text-[30px] md:text-[55px] font-light text-white text-center mb-12">Your Stars, Your Story</h1>
                    </div>

                    <div className="px-8 md:px-16 relative z-10 max-w-[1480px] mx-auto">
                        {/* Price Display */}
                        <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 pb-4 border-b border-white/10 hidden">
                            <div className="mb-2 md:mb-0 text-center md:text-left">
                                <h2 className="text-lg md:text-xl font-bold text-white">Your Custom Star Map</h2>
                                <p className="text-xs md:text-sm text-gray-400">Customize every detail to make it uniquely yours</p>
                            </div>
                            <div className="flex flex-col items-center md:items-end">
                                <div className="text-xl md:text-2xl font-bold text-accent">{formatPrice(calculatePrice())}</div>
                                <div className="text-xs text-gray-400">+{formatPrice(20)} shipping for printed maps</div>
                            </div>
                        </div>

                        {/* Horizontal Tabs with scroll target */}
                        <div ref={mapCreationSectionRef} id="map-creation-section">
                            <HorizontalTabs
                                tabs={tabs}
                                activeTab={activeTab}
                                onChange={handleTabChange}
                            />
                        </div>

                        <div className="grid !grid-cols-1 md:!grid-cols-1 lg:!grid-cols-4 gap-4 md:gap-6 w-full mt-10 md:mt-16">
                            {/* Preview Section - Moved to left side */}
                            {activeTab !== 0 && (
                                <div className="col-span-1 md:col-span-2 order-1 lg:order-2">
                                    <MapPreview
                                        formData={formData}
                                        cityName={cityName}
                                        isGenerating={isGenerating}
                                        loadingProgress={loadingProgress}
                                        mapGenerated={mapGenerated}
                                        mapError={mapError}
                                        handleStarMapError={handleStarMapError}
                                        handleStarMapLoaded={handleStarMapLoaded}
                                        shouldRegenerateMap={shouldRegenerateMap}
                                        starMapRef={starMapRef}
                                        key={mapRenderKey}
                                    />
                                </div>
                            )}

                            {/* Form Inputs - Moved to right side */}
                            <div className={`${activeTab === 0 ? 'lg:col-span-5' : 'lg:col-span-2'} space-y-4 md:space-y-6 order-1 lg:order-2`}>

                                {/* Design Tab */}
                                <TabPanel value={activeTab} index={0}>
                                    <DesignStep
                                        formData={formData}
                                        handleOptionSelect={handleOptionSelect}
                                    />
                                </TabPanel>

                                {/* Format Tab */}
                                <TabPanel value={activeTab} index={1}>
                                    <FormatStep
                                        formData={formData}
                                        handleOptionSelect={handleOptionSelect}
                                        handlePrintTypeChange={handlePrintTypeChange}
                                    />
                                </TabPanel>

                                {/* Moment Tab */}
                                <TabPanel value={activeTab} index={2}>
                                    <MomentStep
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                        handleLocationSelect={handleLocationSelect}
                                        handleToggleCelestialElement={handleToggleCelestialElement}
                                        cityName={cityName}
                                        errors={errors}
                                    />
                                </TabPanel>

                                {/* Text Tab */}
                                <TabPanel value={activeTab} index={3}>
                                    <TextStep
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                    />
                                </TabPanel>

                                {/* Payment Tab */}
                                <TabPanel value={activeTab} index={4}>
                                    <PaymentStep
                                        formData={formData}
                                        calculatePrice={calculatePrice}
                                        isAddingToCart={isAddingToCart}
                                        addedToCart={addedToCart}
                                        handleAddToCart={handleAddToCart}
                                        handleGoToCart={() => navigate('/cart')}
                                        mapGenerated={mapGenerated}
                                        starMapRef={starMapRef}
                                        isGenerating={isGenerating}
                                    />
                                </TabPanel>

                                {/* Navigation buttons */}
                                <div className="flex flex-row gap-4 justify-center items-center mt-6 sm:mt-8">
                                    {activeTab > 0 && activeTab !== 4 && (
                                        <button
                                            onClick={() => {
                                                setActiveTab(prev => prev - 1);
                                                scrollToMapCreationTop();
                                            }}
                                            className="flex items-center justify-center mx-auto w-auto py-2.5 sm:py-3 px-4 sm:px-6 rounded-full font-light transition duration-300 bg-white text-primary hover:bg-gray-100"
                                        >
                                            <span className="text-[14px] md:text-md">Back</span>
                                        </button>
                                    )}

                                    {activeTab !== 4 && activeTab !== 0 && (
                                        <button
                                            onClick={handleGenerateMap}
                                            disabled={isGenerating}
                                            className="flex items-center justify-center mx-auto w-[300px] py-2.5 sm:py-3 px-4 sm:px-6 rounded-full font-light transition duration-300 bg-white text-primary hover:bg-gray-100"
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    <span className="text-[14px] md:text-md text-nowrap">Generating...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                    <span className="text-[14px] md:text-md text-nowrap">Refresh Preview</span>
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {activeTab < 4 && (
                                        <button
                                            onClick={() => {
                                                setActiveTab(prev => prev + 1);
                                                scrollToMapCreationTop();
                                            }}
                                            disabled={activeTab === 2 && !canProceedFromMomentStep()}
                                            className={`flex items-center justify-center mx-auto w-[300px] py-2.5 sm:py-3 px-4 sm:px-6 rounded-full font-light transition duration-300 ${activeTab === 2 && !canProceedFromMomentStep()
                                                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                                : 'bg-white text-primary hover:bg-gray-100'
                                                }`}
                                        >
                                            <span className="text-[14px] md:text-md">Next Step</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="min-h-[120px]"></div>
            </div>
        </ErrorBoundary>
    );
};

export default CreateMapPage; 